import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../i18n/LanguageContext';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Tab, Tabs, CircularProgress, Divider
} from '@mui/material';
import accountingService from '../../services/accounting.service';

interface TabPanelProps {
  children: React.ReactNode;
  value: number;
  index: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <Box hidden={value !== index} sx={{ pt: 2 }}>{value === index && children}</Box>
);

const Reports: React.FC = () => {
  const { t } = useLanguage();
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [trialBalance, setTrialBalance] = useState<any[]>([]);
  const [balanceSheet, setBalanceSheet] = useState<any>(null);
  const [incomeStatement, setIncomeStatement] = useState<any>(null);

  useEffect(() => { loadReport(tab); }, [tab]);

  const loadReport = async (tabIndex: number) => {
    setLoading(true);
    try {
      if (tabIndex === 0) {
        const res = await accountingService.getTrialBalance();
        setTrialBalance(res.data);
      } else if (tabIndex === 1) {
        const res = await accountingService.getBalanceSheet();
        setBalanceSheet(res.data);
      } else if (tabIndex === 2) {
        const res = await accountingService.getIncomeStatement();
        setIncomeStatement(res.data);
      }
    } catch (error) { console.error('Error loading report:', error); }
    finally { setLoading(false); }
  };

  const renderTrialBalance = () => (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow sx={{ bgcolor: '#f5f5f5' }}>
            <TableCell sx={{ fontWeight: 'bold' }}>Account Code</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>Account Name</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }} align="right">Debit</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }} align="right">Credit</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {trialBalance.map((row: any, idx: number) => (
            <TableRow key={idx}>
              <TableCell>{row.accountCode}</TableCell>
              <TableCell>{row.accountName}</TableCell>
              <TableCell>{row.accountType}</TableCell>
              <TableCell align="right">{row.debit > 0 ? `$${row.debit.toFixed(2)}` : ''}</TableCell>
              <TableCell align="right">{row.credit > 0 ? `$${row.credit.toFixed(2)}` : ''}</TableCell>
            </TableRow>
          ))}
          <TableRow sx={{ bgcolor: '#e3f2fd' }}>
            <TableCell colSpan={3} sx={{ fontWeight: 'bold' }}>Total</TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold' }}>
              ${trialBalance.reduce((sum: number, r: any) => sum + r.debit, 0).toFixed(2)}
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold' }}>
              ${trialBalance.reduce((sum: number, r: any) => sum + r.credit, 0).toFixed(2)}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderSection = (title: string, items: any[], color: string) => (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" sx={{ bgcolor: color, p: 1, color: 'white', borderRadius: 1 }}>
        {title}
      </Typography>
      <Table size="small">
        <TableBody>
          {items?.map((item: any, idx: number) => (
            <TableRow key={idx}>
              <TableCell sx={{ pl: 3 }}>{item.accountCode}</TableCell>
              <TableCell>{item.accountName}</TableCell>
              <TableCell align="right">${item.balance.toFixed(2)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );

  const renderBalanceSheet = () => {
    if (!balanceSheet) return null;
    return (
      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" align="center" sx={{ mb: 3 }}>{t('reports.balance_sheet')}</Typography>
        <Typography variant="subtitle1" align="center" sx={{ mb: 3, color: 'text.secondary' }}>
          As of {new Date().toLocaleDateString()}
        </Typography>

        {renderSection('Assets', balanceSheet.assets, '#1976d2')}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
          <Typography variant="h6">Total Assets: ${balanceSheet.totalAssets.toFixed(2)}</Typography>
        </Box>

        <Divider sx={{ my: 2 }} />

        {renderSection('Liabilities', balanceSheet.liabilities, '#d32f2f')}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
          <Typography>Total Liabilities: ${balanceSheet.totalLiabilities.toFixed(2)}</Typography>
        </Box>

        {renderSection('Equity', balanceSheet.equity, '#7b1fa2')}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
          <Typography>Total Equity: ${balanceSheet.totalEquity.toFixed(2)}</Typography>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Typography variant="h6">
            Total Liabilities + Equity: ${(balanceSheet.totalLiabilities + balanceSheet.totalEquity).toFixed(2)}
          </Typography>
        </Box>
      </Paper>
    );
  };

  const renderIncomeStatement = () => {
    if (!incomeStatement) return null;
    return (
      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" align="center" sx={{ mb: 3 }}>{t('reports.income_statement')}</Typography>
        <Typography variant="subtitle1" align="center" sx={{ mb: 3, color: 'text.secondary' }}>
          Period Ending {new Date().toLocaleDateString()}
        </Typography>

        {renderSection('Revenue', incomeStatement.revenue, '#2e7d32')}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Typography variant="h6">Total Revenue: ${incomeStatement.totalRevenue.toFixed(2)}</Typography>
        </Box>

        <Divider sx={{ my: 2 }} />

        {renderSection('Expenses', incomeStatement.expenses, '#ed6c02')}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Typography variant="h6">Total Expenses: ${incomeStatement.totalExpenses.toFixed(2)}</Typography>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2,
          bgcolor: incomeStatement.netIncome >= 0 ? '#e8f5e9' : '#ffebee', borderRadius: 1 }}>
          <Typography variant="h5" color={incomeStatement.netIncome >= 0 ? 'success.main' : 'error.main'}>
            Net Income: ${incomeStatement.netIncome.toFixed(2)}
          </Typography>
        </Box>
      </Paper>
    );
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3 }}>Financial Reports</Typography>

      <Paper sx={{ mb: 3 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab label="Trial Balance" />
          <Tab label="Balance Sheet" />
          <Tab label="Income Statement" />
        </Tabs>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TabPanel value={tab} index={0}>{renderTrialBalance()}</TabPanel>
          <TabPanel value={tab} index={1}>{renderBalanceSheet()}</TabPanel>
          <TabPanel value={tab} index={2}>{renderIncomeStatement()}</TabPanel>
        </>
      )}
    </Box>
  );
};

export default Reports;
